import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { MDX } from "./mdx/tokens";

/**
 * ObondoTrialModal — 7-day free-trial checkout terms.
 * Mandatory legal copy rendered verbatim per Directive.
 */
export default function ObondoTrialModal({ open, onClose, isDark }) {
    const [agreed, setAgreed] = useState(false);
    if (!open) return null;
    const bg = isDark ? "bg-[#0F1524]" : "bg-white";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";

    // Compute trial end date (today + 7d) in ISO short form.
    const trialEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="obondo-trial-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`w-full max-w-lg ${bg} ${text} border ${brd} rounded-2xl p-6 md:p-7 shadow-2xl`}
                data-testid="obondo-trial-modal-body"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: MDX.blueLow }}>
                            Obondo Advanced
                        </div>
                        <div className="font-inter font-bold text-xl leading-tight">7-Day Free Trial</div>
                    </div>
                    <button onClick={onClose} data-testid="obondo-trial-close" aria-label="Close" className={`p-2 rounded-md border ${brd}`}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className={`rounded-md border ${brd} p-4 ${isDark ? "bg-[#090D16]" : "bg-[#F9FAFB]"}`} data-testid="obondo-trial-terms">
                    <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: MDX.blueLow }}>
                        Obondo 7-Day Free Trial Terms
                    </div>
                    <p className={`font-roboto text-[15px] leading-relaxed ${dim}`}>
                        You are starting a 7-day free trial for Obondo Advanced access — <b>no card is required</b> and
                        nothing is ever charged automatically. On <b>{trialEnd}</b>, if you haven't subscribed from your
                        account's Settings › Billing page, your account simply moves back to the free Kimani plan. Your
                        progress stays saved — some features just pause until you subscribe. Subscribing later is a
                        one-click, opt-in action — it never happens without you.
                    </p>
                </div>

                {/* 7-day Trial Preview Timeline — sets expectations & reduces cancel-fear */}
                <div className="mt-4" data-testid="obondo-trial-timeline">
                    <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-2" style={{ color: MDX.blueLow }}>
                        What happens next
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                        <TimelineStep day="Day 1" label="Signup"          detail="Full AI helper unlocked right away" isDark={isDark} />
                        <TimelineStep day="Day 3" label="Check-In"        detail="See how you're doing so far" isDark={isDark} />
                        <TimelineStep day="Day 5" label="Reminder"        detail="2 days left in your trial" isDark={isDark} />
                        <TimelineStep day="Day 7" label="Subscribe Or Switch To Free" detail="No card on file · moves to free plan" isDark={isDark} last />
                    </div>
                </div>

                <label className={`flex items-start gap-2 mt-4 font-roboto text-[15px] ${dim} cursor-pointer`} data-testid="obondo-trial-agree-label">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        data-testid="obondo-trial-agree-checkbox"
                        className="mt-1"
                    />
                    <span>I understand and agree to the trial terms above.</span>
                </label>

                <Link
                    to={agreed ? "/register?intent=obondo_trial" : "#"}
                    onClick={(e) => { if (!agreed) e.preventDefault(); }}
                    data-testid="obondo-trial-cta"
                    style={{ background: agreed ? MDX.greenCta : MDX.grayMid, opacity: agreed ? 1 : 0.5, pointerEvents: agreed ? "auto" : "none" }}
                    className="mt-5 inline-flex items-center justify-center w-full rounded-md font-inter font-bold uppercase tracking-wider text-[17px] py-3 text-white transition-opacity"
                    aria-disabled={!agreed}
                >
                    Continue to Signup
                </Link>
            </div>
        </div>
    );
}

/* ── Timeline step ─────────────────────────────────────── */
function TimelineStep({ day, label, detail, isDark, last }) {
    return (
        <div className="relative">
            <div className="flex items-center gap-1 mb-1.5">
                <div className="h-2 w-2 rounded-full" style={{ background: MDX.blueLowSolid }} />
                {!last && <div className="flex-1 h-px" style={{ background: isDark ? "#1F2937" : "#E5E7EB" }} />}
            </div>
            <div className="font-inter font-bold text-[10px] uppercase tracking-wider" style={{ color: MDX.blueLow }}>{day}</div>
            <div className="font-inter font-semibold text-[12px] leading-tight mt-0.5">{label}</div>
            <div className={`font-roboto text-[10px] leading-tight mt-0.5 ${isDark ? "text-[#9CA3AF]" : "text-[#6B7280]"}`}>{detail}</div>
        </div>
    );
}
